/**
 * Drawer form shell (Reppr Elite forms handoff).
 *
 * Right-side panel over a dimmed backdrop: sticky header (eyebrow, serif title,
 * close, blurb), scrollable body of field groups, sticky footer with a brass
 * primary, plain-text secondary/tertiary and a footnote that states what saving
 * actually does. The body renders inside a <form>, so the primary submits and
 * native validation still works.
 */

import { ReactNode, useEffect, useRef } from 'react'
import styles from './Drawer.module.css'

interface DrawerProps {
  eyebrow: string
  title: string
  blurb?: string
  width?: number
  children: ReactNode
  primary: string
  secondary?: string
  tertiary?: string
  /** Plain statement of what the primary action does. Never marketing. */
  footnote?: string
  busy?: boolean
  onSubmit: (e: React.FormEvent) => void
  onClose: () => void
  onTertiary?: () => void
}

export default function Drawer({
  eyebrow, title, blurb, width = 620, children,
  primary, secondary = 'Cancel', tertiary, footnote, busy,
  onSubmit, onClose, onTertiary,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [busy, onClose])

  return (
    <div className={styles.overlay} onClick={busy ? undefined : onClose} role="presentation">
      <div
        ref={panelRef}
        className={styles.drawer}
        style={{ width, maxWidth: '96vw' }}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="elite-drawer-title"
      >
        <form className={styles.form} onSubmit={onSubmit} noValidate>
          <div className={styles.head}>
            <div className={styles.headRow}>
              <div className={styles.headText}>
                <span className={styles.eyebrow}>{eyebrow}</span>
                <h2 id="elite-drawer-title" className={styles.title}>{title}</h2>
              </div>
              <button
                type="button"
                className={styles.close}
                onClick={onClose}
                disabled={busy}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            {blurb && <p className={styles.blurb}>{blurb}</p>}
          </div>

          <div className={styles.body}>{children}</div>

          <div className={styles.foot}>
            <div className={styles.footRow}>
              <button type="submit" className={styles.primary} disabled={busy}>
                {busy ? 'Saving…' : primary}
              </button>
              <button type="button" className={styles.secondary} onClick={onClose} disabled={busy}>
                {secondary}
              </button>
              {tertiary && onTertiary && (
                <button type="button" className={styles.tertiary} onClick={onTertiary} disabled={busy}>
                  {tertiary}
                </button>
              )}
            </div>
            {footnote && <p className={styles.footnote}>{footnote}</p>}
          </div>
        </form>
      </div>
    </div>
  )
}
